import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso } from '@/utilities/dateutils';

export class HeatPlanBackupItem extends BaseObj {
    public ID: number;
    public __id: number;

    public Bib: number | null;
    public __bib: number | null;

    public HeatPlanID: number;
    public __heatplanid: number;

    public RowNumber: number;
    public __rownumber: number;

    public SignalrSourceID: string | null;
    public __signalrsourceid: string | null;

    public Time: Date | null;
    public __time: Date | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Bib?: number | null;
        EntityState?: EntityState;
        HeatPlanID?: number;
        RowNumber?: number;
        SignalrSourceID?: string | null;
        Time?: Date | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<HeatPlanBackupItem>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('HeatPlanBackupItem tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Bib = values.Bib ?? null;
        this.__bib = values.Bib ?? null;
        Object.defineProperty(this, 'Bib', {
            get(): number | null {
                return this.__bib;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Bib', this.__bib, value);
                this.__bib = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.HeatPlanID = values.HeatPlanID ?? 0;
        this.__heatplanid = values.HeatPlanID ?? 0;
        Object.defineProperty(this, 'HeatPlanID', {
            get(): number {
                return this.__heatplanid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('HeatPlanID', this.__heatplanid, value);
                this.__heatplanid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RowNumber = values.RowNumber ?? 0;
        this.__rownumber = values.RowNumber ?? 0;
        Object.defineProperty(this, 'RowNumber', {
            get(): number {
                return this.__rownumber;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('RowNumber', this.__rownumber, value);
                this.__rownumber = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SignalrSourceID = values.SignalrSourceID ?? '';
        this.__signalrsourceid = values.SignalrSourceID ?? '';
        Object.defineProperty(this, 'SignalrSourceID', {
            get(): string | null {
                return this.__signalrsourceid;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('SignalrSourceID', this.__signalrsourceid, value);
                this.__signalrsourceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Time = values.Time ?? null;
        this.__time = values.Time ?? null;
        Object.defineProperty(this, 'Time', {
            get(): Date | null {
                return this.__time;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('Time', this.__time, value);
                this.__time = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Bib": ${JSON.stringify(this.Bib)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"HeatPlanID": ${JSON.stringify(this.HeatPlanID)}` +
            ',' +
            `"RowNumber": ${JSON.stringify(this.RowNumber)}` +
            ',' +
            `"SignalrSourceID": ${JSON.stringify(this.SignalrSourceID)}` +
            ',' +
            `"Time": ${this.Time ? '"' + formatDateIso(this.Time) + '"' : null}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of HeatPlanBackupItem from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Bib?: number | null;
        HeatPlanID?: number;
        RowNumber?: number;
        SignalrSourceID?: string | null;
        Time?: Date | null;
        EntityState?: 'Added' | 'Unchanged';
    }): HeatPlanBackupItem {
        return new HeatPlanBackupItem(values);
    }

    public static _FromServerJson(json: any): HeatPlanBackupItem {
        return new HeatPlanBackupItem({
            ID: json.ID,
            Bib: json.Bib ?? null,
            HeatPlanID: json.HeatPlanID,
            RowNumber: json.RowNumber,
            SignalrSourceID: json.SignalrSourceID ?? '',
            Time: json.Time ? (json.Time instanceof Date ? json.Time : new Date(json.Time)) : null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of HeatPlanBackupItem from another instance of HeatPlanBackupItem.
     * @param other The HeatPlanBackupItem you want to clone
     * @returns A cloned HeatPlanBackupItem
     */
    public static FromOther(other: HeatPlanBackupItem): HeatPlanBackupItem {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return HeatPlanBackupItem._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return HeatPlanBackupItem.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('ID' | 'Bib' | 'HeatPlanID' | 'RowNumber' | 'SignalrSourceID' | 'Time')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'HeatPlanBackupItem';
}
